import { useTranslation } from 'react-i18next'

import {
  Card,
  CardContent,
  CardHeader,
  CardHeaderContainer,
  CardHeaderTitle,
} from '@ps/ds/components/blocks/Card/Card'
import { Badge } from '@ps/ds/components/primitives/Badge/Badge'
import { Separator } from '@ps/ds/components/primitives/Separator/Separator'
import { Tooltip } from '@ps/ds/components/primitives/Tooltip/Tooltip'
import { InfoSquare } from '@ps/ds/icons/InfoSquare'
import { ClockFading } from 'lucide-react'
import { NS } from 'src/constants/i18n'
import { TreatmentEntryRow } from 'src/features/treatment/components/TreatmentEntryRow'
import type {
  TreatmentBucket,
  TreatmentEntryData,
} from 'src/features/treatment/constants/treatment'
import { TREATMENT_BUCKET } from 'src/features/treatment/constants/treatment'

type TreatmentBucketCardProps = {
  bucket: TreatmentBucket
  isDelayed: boolean
  isVoided: boolean
  entries: TreatmentEntryData[]
}

export const TreatmentBucketCard = ({
  bucket,
  isDelayed,
  isVoided,
  entries,
}: TreatmentBucketCardProps) => {
  const { t } = useTranslation(NS.TRACKING)
  const bucketEntries = entries.filter((entry) => entry.bucket === bucket)

  if (bucketEntries.length === 0) return null

  return (
    <Card className="overflow-hidden">
      <CardHeader size="sm" padding="none" className="min-h-12 border-b bg-alabaster-100 px-4 py-3">
        <CardHeaderContainer>
          <CardHeaderTitle className="text-sm">{t(`treatment.bucket.${bucket}`)}</CardHeaderTitle>
          {bucket === TREATMENT_BUCKET.IN_PROGRESS && isDelayed && !isVoided && (
            <Badge icon={ClockFading} rounded variant="warning" size="sm">
              {t('treatment.delayed')}
            </Badge>
          )}
          {bucket === TREATMENT_BUCKET.EXCLUDED && !isVoided && (
            <Tooltip
              content={t('treatment.excludedInfo')}
              className="w-[200px] whitespace-normal break-words">
              <button
                type="button"
                aria-label={t('treatment.excludedInfo')}
                className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-muted">
                <InfoSquare size={20} />
              </button>
            </Tooltip>
          )}
        </CardHeaderContainer>
      </CardHeader>
      <CardContent className="flex flex-col gap-0 px-4 py-6">
        {bucketEntries.map((entry, index) => (
          <div key={entry.pest.name}>
            <TreatmentEntryRow
              pestImage={entry.pest.image}
              pestName={entry.pest.name}
              status={entry.status}
              tone={entry.tone}
            />
            {index < bucketEntries.length - 1 && <Separator className="my-4" />}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}
